<template>
  <div
    class="row"
    :class="$q.screen.gt.sm ? 'full-row-exactly-with-back-button' : ''"
  >
    <bj-match-agent-modal
      v-if="matchAnAgentModal.visible"
      :match-an-agent="matchAnAgentModal"
      :include-unverified="false"
    />
    <bj-unmatch-agent-modal
      v-if="unmatchAnAgentModal.visible"
      :unmatch-an-agent="unmatchAnAgentModal"
    />
    <bj-change-agent-modal
      v-if="changeAnAgentModal.visible"
      :change-an-agent="changeAnAgentModal"
      :include-unverified="false"
      @agent-changed="agentChanged"
    />
    <bj-take-over-assign-modal
      v-if="takeOverAssignModal.visible"
      :take-over-assign="takeOverAssignModal"
    />
    <bj-create-inquiry-schedule-modal
      v-if="createInquiryScheduleModal.visible"
      :create-inquiry-schedule-modal="createInquiryScheduleModal"
    />
    <bj-change-inquiry-time-modal
      v-if="changeInquiryTimeModal.visible"
      :change-inquiry-time-modal="changeInquiryTimeModal"
    />
    <sd-timeline
      :id="$route.params.id"
      ref="timeline"
      class-name="inquiries"
      timeline-title="Inquiry Timeline"
      :actions="actions"
      @loaded="loaded"
    >
      <div
        slot="before-timeline"
        slot-scope="{ model: inquiry }"
        class="col-md-4 col-xs-12"
      >
        <q-card
          id="inquiry-details"
        >
          <q-card-title>
            Inquiry Details
          </q-card-title>
          <q-list separator>
            <q-item>
              <q-item-main>
                <q-item-tile label>
                  Created at
                </q-item-tile>
                <q-item-tile sublabel>
                  <SdDatetime
                    :value="inquiry.created_at"
                    :market-id="inquiry.property && inquiry.property.market_id"
                    :timezone="inquiry.property && inquiry.property.timezone"
                  />
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-main>
                <q-item-tile label>
                  Updated at
                </q-item-tile>
                <q-item-tile sublabel>
                  <SdDatetime
                    :value="inquiry.updated_at"
                    :market-id="inquiry.property && inquiry.property.market_id"
                    :timezone="inquiry.property && inquiry.property.timezone"
                  />
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-main>
                <q-item-tile label>
                  Source
                </q-item-tile>
                <q-item-tile sublabel>
                  <q-chip>{{ inquiry.source.name }}</q-chip>
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-main>
                <q-item-tile label>
                  Status
                </q-item-tile>
                <q-item-tile sublabel>
                  <q-chip :color="inquiry.status === $config.inquiryStatuses.MATCHING_FAILED ? 'negative' : ''">
                    {{ inquiry.status.toUpperCase() }}
                  </q-chip>
                  <q-icon
                    v-if="inquiry.uninterested_at"
                    v-tippy
                    color="negative"
                    name="cancel"
                    title="Marked as uninterested"
                  />
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-main>
                <q-item-tile label>
                  Lead
                </q-item-tile>
                <q-item-tile sublabel>
                  <SdLeadFullName
                    :lead="inquiry.lead"
                  />
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-main>
                <q-item-tile label>
                  Agent
                </q-item-tile>
                <q-item-tile sublabel>
                  <span v-if="inquiry.tour && inquiry.tour.user">
                    <template v-if="inquiry.tour.user.role === 'agent'">
                      <router-link :to="{ name: 'app.agents', params: { id: inquiry.tour.user.id }}">
                        {{ inquiry.tour.user.full_name }}
                      </router-link>
                    </template>
                    <template v-else>
                      <router-link :to="{ name: 'app.businesses.profile.user', params: { id: inquiry.tour.user.business_id, userId: inquiry.tour.user.id } }">
                        {{ inquiry.tour.user.full_name }}
                      </router-link>
                    </template>
                  </span>
                  <span v-else>
                    Unassigned
                  </span>
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item v-if="inquiry.tour">
              <q-item-main>
                <q-item-tile label>
                  Tour Status
                </q-item-tile>
                <q-item-tile sublabel>
                  <span v-if="inquiry.tour.is_taken_over">
                    Taken Over -
                  </span>
                  <span v-else-if="inquiry.tour.is_self_showing">
                    Self Showing -
                  </span>
                  <span v-if="inquiry.tour">
                    {{ getHumanScheduleStatus(inquiry.tour.status) }}
                  </span>
                  <div
                    v-if="inquiry.tour && inquiry.tour.device_code"
                    class="font-weight-bold"
                  >
                    <span>
                      Used device code: {{ inquiry.tour.device_code }}
                    </span>
                  </div>
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item v-if="!inquiry.property">
              <q-item-main>
                <q-item-tile label>
                  Interested in property
                </q-item-tile>
                <q-item-tile sublabel>
                  {{ inquiry.interested_in_property }}
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item v-if="inquiry.property">
              <q-item-main>
                <q-item-tile label>
                  Property name
                </q-item-tile>
                <q-item-tile sublabel>
                  <router-link
                    :to="{ name: 'app.properties.combined', params: { id: inquiry.property.id } }"
                  >
                    {{ inquiry.property.short_address }}
                  </router-link>
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item v-if="inquiry.unit && inquiry.unit.name">
              <q-item-main>
                <q-item-tile label>
                  Unit name
                </q-item-tile>
                <q-item-tile sublabel>
                  {{ inquiry.unit.name }}
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item v-if="inquiry.tour">
              <q-item-main>
                <q-item-tile label>
                  Tour
                </q-item-tile>
                <q-item-tile sublabel>
                  <q-btn
                    rounded
                    outline
                    size="sm"
                    color="primary"
                    :label="inquiry.tour.id"
                    @click="redirect('app.tours', { id: inquiry.tour.id })"
                  />
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item v-if="inquiry.is_video || get(inquiry, 'schedule_inquiry.schedule.is_video')">
              <q-item-main>
                <q-item-tile label>
                  <component
                    :is="get(inquiry,'schedule_inquiry.schedule.meeting_room.deleted_at') ? 'span' : 'a'"
                    v-if="get(inquiry,'schedule_inquiry.schedule.is_video')"
                    :href="`https://${get(inquiry,'schedule_inquiry.schedule.meeting_room.url')}`"
                    target="_blank"
                  >
                    Live Video Tour
                    <q-chip
                      dense
                      color="blue"
                      text-color="black"
                      style="margin-left: 5px; padding: 0.25rem 0.5rem;"
                    >
                      <i class="q-icon fa fa-video" />
                    </q-chip>
                  </component>
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item v-else-if="get(inquiry, 'schedule_inquiry.schedule.is_open_house')">
              <q-item-main>
                <q-item-tile label>
                  Open House
                  <q-chip
                    dense
                    color="blue"
                    text-color="black"
                    style="margin-left: 5px; padding: 0.25rem 0.5rem;"
                  >
                    <i class="q-icon fa fa-users" />
                  </q-chip>
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item v-if="inquiry.schedule_inquiry">
              <q-item-main>
                <q-item-tile label>
                  Tour at
                </q-item-tile>
                <q-item-tile sublabel>
                  <SdDatetime
                    :value="inquiry.schedule_inquiry.schedule.tour_at"
                    :market-id="inquiry.property.market_id"
                    :timezone="inquiry.property.timezone"
                  />
                </q-item-tile>
              </q-item-main>
            </q-item>
            <q-item v-if="inquiry.persona_identification_url">
              <q-item-main>
                <q-item-tile label>
                  <a
                    :href="inquiry.persona_identification_url"
                    target="_blank"
                  >
                    Persona Inquiry
                  </a>
                </q-item-tile>
              </q-item-main>
            </q-item>
          </q-list>
        </q-card>
        <p />
        <q-card
          id="lead-identity-history"
        >
          <q-card-title>Inquiry - Lead Identity History: ({{ leadIdentityHistory.length }})</q-card-title>
          <q-card-separator />
          <q-card-main class="helper-text">
            <p>Displays only the relevant identity records related to the current inquiry.</p>
            <p>
              To view all identity history records, visit
              <router-link :to="{ name: 'app.leads.profile', params: { id: inquiry.lead.id } }">
                {{ inquiry.lead.full_name }}
              </router-link> lead profile page.
            </p>
          </q-card-main>
          <q-card-separator />
          <q-card-main
            v-if="leadIdentityHistory.length"
            class="relative-position scrollable-wrapper"
          >
            <SdLeadIdentityHistoryTimeline :identity-history="leadIdentityHistory" />
          </q-card-main>
        </q-card>
      </div>
    </sd-timeline>
  </div>
</template>

<script>
import get from 'lodash.get';
import SdLeadFullName from '@/components/common/SdLeadFullName';
import { ScheduleConfirmationStatuses } from '@/constants/Schedule';
import { ShowingTypes } from '@/constants/ShowingProfile';
import { canAdminMarkInquiryAsIdentified } from '@/utils/InquiryUtil';
import { getHumanScheduleStatus } from '@/utils/ScheduleUtil';
import SdTimeline from '../../components/timeline';
import BjMatchAgentModal from '../../plugins/bluejay/components/match-agent-modal';
import BjUnmatchAgentModal from '../../plugins/bluejay/components/unmatch-agent-modal';
import BjChangeAgentModal from '../../plugins/bluejay/components/modals/change-agent-modal';
import BjTakeOverAssignModal from '../../plugins/bluejay/components/modals/take-over-assign-modal';
import BjCreateInquiryScheduleModal from '../../plugins/bluejay/components/modals/create-inquiry-schedule-modal';
import BjChangeInquiryTimeModal from '../../plugins/bluejay/components/modals/change-inquiry-time-modal';
import SdLeadIdentityHistoryTimeline from '../../components/common/SdLeadIdentityHistoryTimeline';

export default {
  components: {
    SdLeadFullName,
    SdTimeline,
    BjMatchAgentModal,
    BjUnmatchAgentModal,
    BjCreateInquiryScheduleModal,
    BjChangeAgentModal,
    BjTakeOverAssignModal,
    BjChangeInquiryTimeModal,
    SdLeadIdentityHistoryTimeline,
  },
  data() {
    const base_modal_payload = {
      visible: false,
      loading: false,
      payload: {
        inquiry: null,
        query: '',
        agent: null,
      },
    };
    return {
      inquiry: null,
      matchAnAgentModal: this.clone(base_modal_payload),
      unmatchAnAgentModal: this.clone(base_modal_payload),
      takeOverAssignModal: this.clone(base_modal_payload),
      changeAnAgentModal: this.clone(base_modal_payload),
      createInquiryScheduleModal: this.clone(base_modal_payload),
      changeInquiryTimeModal: this.clone(base_modal_payload),
    };
  },
  computed: {
    leadIdentityHistory() {
      return get(this.inquiry, 'lead.identity_history') || [];
    },
    actions() {
      if (!this.inquiry) {
        return [];
      }

      const isInProgress = this.inquiry.status === this.$config.inquiryStatuses.IN_PROGRESS;
      const isScheduled = this.inquiry.status === this.$config.inquiryStatuses.SCHEDULED;
      const isPending = this.inquiry.status === this.$config.inquiryStatuses.PENDING;
      const tourTimeOut = get(this.inquiry, 'tour.status') === this.$config.tourStatuses.TIME_OUT;
      const isShowingsAvailableForMarket = get(this.inquiry, 'property.market.showing_agents_available');
      const isVideo = get(this.inquiry, 'schedule_inquiry.schedule.is_video', false);
      const isSelfShowing = get(this.inquiry, 'schedule_inquiry.schedule.tour.is_self_showing', false);
      const isVideoTourPermitted = !!get(this.inquiry, 'property.business.video_tours_enabled_at', true);
      const isOpenHouse = this.inquiry.schedule_inquiry && this.inquiry.schedule_inquiry.schedule && this.inquiry.schedule_inquiry.schedule.is_open_house;
      const isInProgressOrScheduled = isInProgress || isScheduled;
      const isShowami = get(this.inquiry, 'schedule_inquiry.schedule.is_showami', false);
      const isScheduledAndHavePendingTour = isScheduled && this.inquiry.tour && this.inquiry.tour.status === 'pending';
      const isLeadBlocked = Boolean(this.inquiry.lead.is_blocked);
      const isTakenOverTour = get(this.inquiry, 'schedule_inquiry.schedule.tour.is_taken_over');
      const isConcurrentSchedule = get(this.inquiry, 'schedule_inquiry.schedule.is_concurrent');
      const isAllowBypassGeolocationCheck = this.inquiry.allow_bypass_geolocation_check;
      const isShowingProfilePublishListing = get(this.inquiry, 'unit.active_showing_profile.type') === ShowingTypes.PUBLISH_LISTING;
      const hasActiveShowingProfile = get(this.inquiry, 'unit.active_showing_profile');
      const currentAssignedAgentIsAgent = get(this.inquiry, 'tour.user.role') === this.$config.roles.AGENT;

      let disableChangeTourType = true;
      if (isInProgressOrScheduled && !isSelfShowing) {
        disableChangeTourType = isVideo ? false : !isVideoTourPermitted;
      }

      const actions = [
        {
          label: 'Send text',
          disabled: !this.inquiry.lead.phone_number,
          handler: () => this.openSendTextPrompt(),
        },
        {
          label: 'Change tour time',
          disabled: isShowami || isOpenHouse || !isInProgressOrScheduled || isTakenOverTour || isLeadBlocked || isConcurrentSchedule,
          handler: () => this.openChangeInquiryTimeModal(this.inquiry),
        },
        {
          label: !(isInProgressOrScheduled && !isSelfShowing) || isVideo ? 'Change to in-person tour' : 'Change to video tour',
          disabled: isOpenHouse || disableChangeTourType || isLeadBlocked,
          handler: () => this.changeScheduleType(this.inquiry, isVideo),
        },
        {
          label: 'Confirm on behalf of Lead',
          disabled: !(isInProgressOrScheduled && this.inquiry.scheduleConfirmationStatus === ScheduleConfirmationStatuses.CONFIRMATION_STATUS_NOT_CONFIRMED)
          || isLeadBlocked,
          handler: () => this.confirmOnBehalfOfLead(this.inquiry),
        },
        {
          label: 'Schedule tour',
          disabled: !(isPending && hasActiveShowingProfile) || isLeadBlocked || !!this.inquiry.identification_failed_at || isShowingProfilePublishListing,
          handler: () => this.openScheduleTourModal(this.inquiry),
        },
        {
          label: 'Mark as uninterested',
          disabled: !(isPending && !this.inquiry.uninterested_at),
          handler: () => this.markAsUninterested(this.inquiry),
        },
        {
          label: 'Reset pre-screening answers',
          disabled: !(isPending && !!this.inquiry.screening_criteria_attempts),
          handler: () => this.openResetScreeningAnswers(this.inquiry),
        },
        {
          label: 'Add admin comment',
          disabled: false,
          handler: () => this.openSendCommentPrompt(),
        },
        //
        // {
        //   label: 'Take-over assign',
        //   disabled: !(isInProgressOrScheduled && !get(this.inquiry.schedule_inquiry, 'schedule.tour.is_taken_over')),
        //   handler: () => this.openTakeOverAssignModal(this.inquiry),
        // },
        {
          label: get(this.inquiry, 'schedule_inquiry.schedule.is_open_house') ? 'Cancel Open House' : 'Cancel Tour',
          disabled: !(isInProgressOrScheduled && get(this.inquiry, 'schedule_inquiry.schedule')),
          handler: () => this.cancelTour(this.inquiry),
        },
        {
          label: 'Match an Agent',
          disabled: !(isInProgress && isShowingsAvailableForMarket) || isLeadBlocked,
          handler: () => this.openMatchAnAgentModal(this.inquiry),
        },
        {
          label: 'Submit tour report',
          disabled: !isScheduledAndHavePendingTour && !tourTimeOut,
          url: this.inquiry.tour ? `/app/tours/${this.inquiry.tour.id}/after-tour-survey` : '',
        },
        {
          label: 'Release agent',
          disabled: isShowami || (!(isScheduledAndHavePendingTour && get(this.inquiry.schedule_inquiry, 'schedule.tour.user_id') && !isTakenOverTour)),
          handler: () => this.openUnmatchAnAgentModal(this.inquiry),
        },
        // {
        //   label: 'Release Showami',
        //   disabled: !isShowami,
        //   handler: () => this.releaseShowami(this.inquiry),
        // },
        {
          label: 'Change Agent',
          disabled: isShowami || !(isScheduled && this.inquiry.tour && isShowingsAvailableForMarket && currentAssignedAgentIsAgent),
          handler: () => this.openChangeAnAgentModal(this.inquiry),
        },
        {
          label: 'Enter lead page',
          disabled: !(this.inquiry.status !== this.$config.inquiryStatuses.NEW && this.inquiry.status !== this.$config.inquiryStatuses.MATCHING_FAILED)
          || isLeadBlocked,
          url: `${this.$config.api.baseUrl}lead/inquiry-ui/${this.inquiry.uuid}?admin=1`,
        },
        {
          label: 'Bypass geo-location',
          disabled: !isAllowBypassGeolocationCheck,
          handler: () => this.openBypassGeolocationPromt(this.inquiry),
        },
        {
          label: 'Mark as identified',
          disabled: !canAdminMarkInquiryAsIdentified(this.inquiry),
          handler: () => this.markAsIdentified(this.inquiry),
        },
      ];

      // We want to show the non-disabled first
      actions.sort((a, b) => Number(a.disabled) - Number(b.disabled));

      return actions;
    },
  },
  mounted() {
    this.$root.$on('schedule-added', () => {
      this.createInquiryScheduleModal.visible = false;
      window.location.reload();
    });
    this.$root.$on('schedule-time-changed', () => {
      this.changeInquiryTimeModal.visible = false;
      window.location.reload();
    });
    this.$root.$on('agent-matched', () => {
      this.matchAnAgentModal.visible = false;
      window.location.reload();
    });
    this.$root.$on('close-unmatch-agent-modal', (reload = false) => {
      this.unmatchAnAgentModal.visible = false;
      if (reload) {
        window.location.reload();
      }
    });
    this.$root.$on('take-over-assigned', () => {
      this.takeOverAssignModal.visible = false;
      window.location.reload();
    });
  },
  methods: {
    get,
    getHumanScheduleStatus,
    init() {
      this.$refs.timeline.init();
    },
    loaded(inquiry) {
      this.inquiry = inquiry;
      this.inquiry.tour = inquiry.schedule_inquiry && inquiry.schedule_inquiry.schedule.tour;
    },
    openSendTextPrompt(text = '') {
      this.uiPrompt('Send text to lead', 'Choose text message to send to lead', 'textarea', text).then((message) => {
        if (message && message.length) {
          this.uiConfirm(`Is that the text you want to send? "${message}"`, 'Text review', 'Yes, send', 'No, I want to rewrite').then(() => {
            this.api().request('POST', `admin/leads/${this.inquiry.lead.id}/notifications`, { message, channel: 'sms' })
              .then(() => {
                this.uiNotify('The platform will send the text as soon as possible!');
              }).catch((error) => {
                this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative');
              });
          }).catch(() => {
            this.openSendTextPrompt(message);
          });
        } else {
          this.uiNotify('You must fill text message', 'negative');
        }
      });
    },
    openBypassGeolocationPromt() {
      this.uiConfirm('Are you sure you want to bypass lead\'s location and codebox serial number verification? confirming will grant the access code to the lead once they refresh their page.').then(() => {
        this.api().request('POST', `admin/inquiries/${this.inquiry.id}/bypass`)
          .then(() => {
            this.uiNotify('The platform will allow the lead to continue without location and codebox serial number verification');
            window.location.reload();
          }).catch((error) => {
            this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative');
          });
      });
    },
    releaseShowami(inquiry) {
      this.uiConfirm('Are you sure you want to release Showami from this schedule? Penalties will apply if showami agent is already found and cancelled less than 2 hours before tour starts')
        .then(() => {
          this.api().request('DELETE', `admin/showami/${inquiry.schedule_inquiry.schedule_id}`).then(() => {
            this.uiNotify('Unassigned Showami from schedule');
            this.init();
          }).catch((error) => {
            this.uiNotify(error.message || 'Error occurred', 'negative');
          });
        });
    },
    openSendCommentPrompt(text = '') {
      this.uiPrompt('Add comment to the inquiry', '', 'textarea', text, 'Add').then((comment) => {
        if (comment && comment.length) {
          this.api().request('POST', `inquiries/${this.inquiry.id}/comment`, {
            comment,
          }).then(() => {
            this.uiNotify('The comment was added successfully');
          }).catch((error) => {
            this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative');
          });
        } else {
          this.uiNotify('You must fill text message', 'negative', 5000);
        }
      });
    },
    openScheduleTourModal(inquiry) {
      this.createInquiryScheduleModal.payload.inquiry = inquiry;
      this.createInquiryScheduleModal.visible = true;
    },
    openChangeInquiryTimeModal(inquiry) {
      this.changeInquiryTimeModal.payload.inquiry = inquiry;
      this.changeInquiryTimeModal.visible = true;
    },
    openMatchAnAgentModal(inquiry) {
      this.matchAnAgentModal.visible = true;
      this.matchAnAgentModal.payload.inquiry = inquiry;
    },
    openUnmatchAnAgentModal(inquiry) {
      this.unmatchAnAgentModal.visible = true;
      this.unmatchAnAgentModal.payload.inquiry = inquiry;
    },
    openTakeOverAssignModal(inquiry) {
      this.takeOverAssignModal.visible = true;
      this.takeOverAssignModal.payload.inquiry = inquiry;
    },
    openChangeAnAgentModal(inquiry) {
      this.changeAnAgentModal.visible = true;
      this.changeAnAgentModal.payload.inquiry = inquiry;
    },
    markAsUninterested(inquiry) {
      this.uiConfirm('Are you sure you want to mark this inquiry as "uninterested"? No further reminders and messages will be sent to the lead')
        .then(() => {
          this.api().request('PUT', `admin/inquiries/${inquiry.id}`, {
            is_uninterested: true,
          }).then(() => {
            this.uiNotify('Inquiry marked as uninterested');
          });
        });
    },
    confirmOnBehalfOfLead(inquiry) {
      this.$q.dialog({
        title: 'Are you sure you want to confirm on behalf of the lead?',
        ok: true,
        cancel: true,
      }).then(() => {
        this.api().request('POST', `schedule-inquiries/${inquiry.schedule_inquiry.id}/confirm`).then(() => {
          this.uiNotify('Confirmed successfully');
          window.location.reload();
        }).catch((error) => {
          this.uiNotify(error.message || 'Error occurred', 'negative');
        });
      });
    },
    cancelTour(inquiry) {
      const hasTourMessage = inquiry.tour ? 'the tour for ' : '';
      this.uiConfirm(`Are you sure you want to cancel ${hasTourMessage}this inquiry?`).then(() => {
        this.api().request('DELETE', `admin/inquiries/${inquiry.id}`).then(() => {
          this.uiNotify('Tour Cancelled');
          window.location.reload();
        }).catch((error) => {
          this.loading = false;
          this.uiNotify(this.getFirstError(error) || error.message || 'Error occurred', 'negative', 5000);
        });
      });
    },
    markAsIdentified(inquiry) {
      this.uiConfirm('Are you sure you want to mark this inquiry as identified?')
        .then(() => {
          this.api().request('PUT', `admin/inquiries/${inquiry.id}`, {
            is_identified: true,
          }).then(() => {
            this.uiNotify('Inquiry marked as identified');
            window.location.reload();
          }).catch((error) => {
            this.uiNotify(error.message || 'Error occurred', 'negative');
          });
        });
    },
    openResetScreeningAnswers(inquiry) {
      this.uiConfirm('The prospect will be able to restart the scheduling process and will have a chance to answer the pre-screening questions again. They will not be notified. Are you sure?')
        .then(() => {
          this.api().request('PUT', `admin/inquiries/${inquiry.id}`, {
            screening_criteria_attempts: [],
          }).then(() => {
            this.uiNotify('Successfully reset Inquiry pre-screening answers');
          }, (error) => {
            this.uiNotify(error.message, 'negative');
          });
        });
    },
    async changeScheduleType(inquiry, isVideo) {
      const scheduleChangeLabel = isVideo ? 'in-person tour' : 'video tour';
      if (!isVideo && inquiry.property.business.video_tours_enabled_at === null) {
        const business = inquiry.property.business;
        const alertMessage = `Video tours are currently disabled for business ${business.name || business.id}, only continue if override was pre approved by PM`;
        await this.uiAlert(alertMessage, 'Video tours are disabled');
      }
      await this.uiConfirm(`Are you sure you want to change this schedule to a ${scheduleChangeLabel}?`);
      const scheduleId = get(inquiry, 'schedule_inquiry.schedule.id');
      this.api().request('PUT', `schedules/${scheduleId}`, {
        is_video: !isVideo,
        inquiry_id: inquiry.id,
      }).then(() => {
        this.uiNotify(`Schedule changed to ${scheduleChangeLabel}`);
        this.inquiry.schedule_inquiry.schedule.is_video = !isVideo;
      }, (error) => {
        this.uiNotify(error.message, 'negative');
      });
    },
    agentChanged() {
      this.changeAnAgentModal.visible = false;
      window.location.reload();
    },
  },
};
</script>

<style lang="scss">
  #inquiry-details {
    display: flex;
    flex-direction: column;

    .q-list {
      overflow: auto;
    }
  }

  #lead-identity-history {

    .scrollable-wrapper {
      overflow-y: scroll;
      max-height: 70vh;
    }

    .helper-text{
      font-size: 14px;

      > p {
        font-size: inherit;

        &+p {
          margin-bottom: 0;
        }
      }
    }
  }

  #card-main-timeline {
    height: calc(100% - 64px);
    overflow: auto;
  }

    .q-timeline-content {
        ul {
            margin: 0;
            padding-inline-start: 1.5rem;
        }
    }

    .lead_disqualified  {
        .q-timeline-subtitle {
            padding-top: 0.5rem;
            margin: 0;
        }

        .q-timeline-content {
            background: #FBDFDB;
            border-radius: 1rem;
            padding: 1rem;
            width: 60%;

            > .row > .col-auto {
                display: none;
            }
        }
    }

  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: #DFE5EB;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #cce6eb;
  }
</style>
